// Checklist DEFINITIONS are server-authoritative now (see
// src/utils/checklistMapper.js + src/actions/checklists.js) — this file only
// keeps the ANSWER draft layer, used for: (a) photo answers, which aren't
// submitted to the backend yet (deferred until the report photo-upload
// contract is ready), and (b) resuming an in-progress, not-yet-submitted
// fill-out if the user closes the dialog before completing it.
import { getItem, setItem } from "./DataProtection";

const ANSWER_STORAGE_PREFIX = "maintenance-checklist-answers";

const sanitizeDraftAttachment = (attachment = {}, index = 0) => ({
  nodeid: attachment.nodeid || "",
  index:
    attachment.index !== undefined ? attachment.index : Date.now() + index,
  fileid: attachment.fileid || "",
  url: attachment.url || attachment.previewUrl || "",
  mime: attachment.mime || "",
  bucketid: attachment.bucketid || "",
  fileversion: attachment.fileversion || "",
  title: (attachment.title || "").trim(),
  description: (attachment.description || "").trim(),
  uploaded: Boolean(attachment.uploaded || attachment.url),
  uploadProgress:
    attachment.uploadProgress !== undefined
      ? Number(attachment.uploadProgress) || 0
      : attachment.url
      ? 100
      : 0,
  uploadError: attachment.uploadError || ""
});

const sanitizeDraftAttachmentList = attachments =>
  Array.isArray(attachments)
    ? attachments.map((attachment, index) =>
        sanitizeDraftAttachment(attachment, index)
      )
    : [];

// With a Meldung, its own key only: every Meldung of a recurring task shares
// the task's ids, so a task-wide draft would carry one Meldung's answers into
// the next. The task keys remain for callers without a Meldung.
export const buildMaintenanceChecklistAnswerStorageKeys = ({
  notificationnodeid,
  maintenancetaskid,
  maintenancetasknodeid,
  workflowstepnodeid
} = {}) =>
  notificationnodeid
    ? [`${ANSWER_STORAGE_PREFIX}:notification:${notificationnodeid}`]
    : [
        maintenancetaskid && `${ANSWER_STORAGE_PREFIX}:id:${maintenancetaskid}`,
        maintenancetasknodeid &&
          `${ANSWER_STORAGE_PREFIX}:node:${maintenancetasknodeid}`,
        workflowstepnodeid &&
          `${ANSWER_STORAGE_PREFIX}:workflow:${workflowstepnodeid}`
      ].filter(Boolean);

export const saveMaintenanceChecklistAnswersToStorage = (ids, answers) => {
  const keys = buildMaintenanceChecklistAnswerStorageKeys(ids);
  const safeAnswers =
    answers && typeof answers === "object"
      ? Object.entries(answers).reduce((accumulator, [checklistId, checklist]) => {
          accumulator[checklistId] = {
            comment: checklist?.comment || "",
            completed: Boolean(checklist?.completed),
            entries: Object.entries(checklist?.entries || {}).reduce(
              (entryAccumulator, [entryId, entryAnswer]) => {
                entryAccumulator[entryId] = {
                  checked: Boolean(entryAnswer?.checked),
                  numberValue: entryAnswer?.numberValue || "",
                  textValue: entryAnswer?.textValue || "",
                  attachmentImages: sanitizeDraftAttachmentList(
                    entryAnswer?.attachmentImages
                  ),
                  attachmentVideos: sanitizeDraftAttachmentList(
                    entryAnswer?.attachmentVideos
                  ),
                  attachmentDocs: sanitizeDraftAttachmentList(
                    entryAnswer?.attachmentDocs
                  )
                };
                return entryAccumulator;
              },
              {}
            )
          };
          return accumulator;
        }, {})
      : {};

  keys.forEach(key => {
    setItem(key, safeAnswers);
  });

  return safeAnswers;
};

export const loadMaintenanceChecklistAnswersFromStorage = ids => {
  const keys = buildMaintenanceChecklistAnswerStorageKeys(ids);

  for (const key of keys) {
    const storedValue = getItem(key);

    if (storedValue && typeof storedValue === "object") {
      return storedValue;
    }
  }

  return {};
};
